import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ExclamationTriangleIcon,
  ArrowPathIcon,
  PhoneIcon,
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
  CreditCardIcon,
  ShieldCheckIcon,
  ArrowRightIcon,
  HomeIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

const PaymentFailurePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  
  // Extract error information from URL
  const errorCode = searchParams.get('error');
  const errorMessage = searchParams.get('message');
  const amount = searchParams.get('amount');
  const currency = searchParams.get('currency');
  const packageName = searchParams.get('package') || 'Bali Experience Package';
  const retryUrl = searchParams.get('retry') || '/book-now';

  const getErrorDetails = (code: string | null) => {
    switch (code) {
      case 'insufficient_funds':
        return {
          title: 'Insufficient Funds',
          description: 'Your payment method doesn\'t have enough funds for this transaction.',
          icon: '💳',
          suggestion: 'Please try a different payment method or add funds to your account.'
        };
      case 'card_declined':
        return {
          title: 'Card Declined',
          description: 'Your card was declined by your bank or card issuer.',
          icon: '🚫',
          suggestion: 'Please contact your bank or try a different payment method.'
        };
      case 'expired_card':
        return {
          title: 'Expired Card',
          description: 'The payment method you used has expired.',
          icon: '⏰',
          suggestion: 'Please update your payment method with current details.'
        };
      case 'network_error':
        return {
          title: 'Network Error',
          description: 'There was a temporary connection issue while processing your payment.',
          icon: '🌐',
          suggestion: 'Please check your internet connection and try again.'
        };
      case 'payment_cancelled':
        return {
          title: 'Payment Cancelled',
          description: 'You cancelled the payment process.',
          icon: '❌',
          suggestion: 'No charges were made. You can try again when ready.'
        };
      default:
        return {
          title: 'Payment Failed',
          description: errorMessage || 'We encountered an issue while processing your payment.',
          icon: '⚠️',
          suggestion: 'Please try again or contact our support team for assistance.'
        };
    }
  };

  const errorDetails = getErrorDetails(errorCode);

  return (
    <>
      <Helmet>
        <title>Payment Failed - We're Here to Help | Bali Getaway</title>
        <meta name="description" content="Payment unsuccessful. Don't worry, we're here to help you complete your booking." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 px-4 py-16">
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto"
        >
          {/* Error Header */}
          <div className="text-center mb-16">
            <div className="relative inline-block">
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200, damping: 15 }}
                className="w-32 h-32 bg-gradient-to-br from-red-500 to-orange-600 rounded-[2.5rem] flex items-center justify-center mx-auto mb-8 shadow-2xl"
              >
                <ExclamationTriangleIcon className="w-16 h-16 text-white" />
              </motion.div>
              <div className="absolute -top-2 -right-2 text-4xl animate-bounce">{errorDetails.icon}</div>
            </div>
            
            <h1 className="text-5xl lg:text-7xl font-black text-gray-900 mb-6 tracking-tight">
              {errorDetails.title}
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed font-medium">
              Don't worry - <span className="text-gray-900 font-bold">no charges were made</span> to your account. We're here to help you fix this.
            </p>
            <div className="inline-flex items-center px-8 py-3 bg-red-100 text-red-800 rounded-2xl text-lg font-black uppercase tracking-tighter border border-red-200 shadow-sm">
              <ExclamationTriangleIcon className="w-6 h-6 mr-3" />
              Payment Unsuccessful
            </div>
          </div>

          {/* Error Details Card */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-[3rem] shadow-2xl overflow-hidden border border-gray-100 mb-12"
          >
            <div className="bg-gradient-to-r from-red-600 to-orange-700 text-white p-10 flex justify-between items-center">
              <div>
                <h2 className="text-3xl font-black mb-2 tracking-tight">What Happened?</h2>
                <p className="text-red-100 text-lg font-medium opacity-90">Issue diagnostics & next steps</p>
              </div>
              <div className="hidden md:block">
                <ExclamationTriangleIcon className="w-16 h-16 text-white/20" />
              </div>
            </div>
            
            <div className="p-10">
              <div className="max-w-3xl mx-auto space-y-8">
                <div className="bg-gray-50 rounded-3xl p-8 border border-gray-100 relative overflow-hidden">
                  <p className="text-gray-900 text-xl font-bold mb-6 leading-relaxed relative z-10">{errorDetails.description}</p>
                  <div className="bg-white rounded-2xl p-6 border border-red-100 shadow-sm relative z-10">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <SparklesIcon className="w-6 h-6 text-red-600" />
                      </div>
                      <div>
                        <h3 className="font-black text-red-900 mb-1 uppercase tracking-tight text-sm">Recommended Solution</h3>
                        <p className="text-red-800 font-medium">{errorDetails.suggestion}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Transaction Details */}
                {(amount || packageName) && (
                  <div className="space-y-6">
                    <h3 className="text-xl font-black text-gray-900 flex items-center uppercase tracking-tight">
                      <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center mr-3">
                        <CreditCardIcon className="w-5 h-5 text-gray-600" />
                      </div>
                      Transaction Summary
                    </h3>
                    <div className="bg-gray-50 rounded-3xl p-6 space-y-4 border border-gray-100">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500 font-bold text-xs uppercase tracking-wider">Target Package</span>
                        <span className="font-black text-gray-900">{packageName}</span>
                      </div>
                      {amount && currency && (
                        <div className="flex justify-between items-center py-4 border-y border-gray-200/60">
                          <span className="text-gray-500 font-bold text-xs uppercase tracking-wider">Attempted Amount</span>
                          <span className="font-black text-gray-900 text-2xl">{currency} {parseFloat(amount).toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500 font-bold text-xs uppercase tracking-wider">Timestamp</span>
                        <span className="font-bold text-gray-900">{new Date().toLocaleString('en-AU', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500 font-bold text-xs uppercase tracking-wider">Current Status</span>
                        <span className="inline-flex items-center px-4 py-1.5 bg-red-100 text-red-700 rounded-full text-xs font-black uppercase tracking-tighter border border-red-200">
                          <ExclamationTriangleIcon className="w-4 h-4 mr-1.5" />
                          Failed / Rejected
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>

          {/* Solutions Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {/* Try Again */}
            <motion.div 
              whileHover={{ y: -5 }}
              className="bg-white rounded-[3rem] p-10 shadow-xl border border-gray-100 relative overflow-hidden group"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-green-50 rounded-full -mr-16 -mt-16 opacity-50 group-hover:scale-110 transition-transform"></div>
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-green-200">
                  <ArrowPathIcon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-black text-gray-900 mb-4 tracking-tight">Try Again</h3>
                <p className="text-gray-500 font-medium mb-8 leading-relaxed">Most payment issues are temporary network glitches. One more attempt usually works.</p>
                <Link
                  to={retryUrl}
                  className="inline-flex items-center justify-center w-full px-8 py-4 bg-gradient-to-r from-green-600 to-emerald-700 text-white font-black rounded-2xl transition-all shadow-xl shadow-green-100 active:scale-95"
                >
                  <ArrowPathIcon className="w-5 h-5 mr-2" />
                  RETRY PAYMENT
                </Link>
              </div>
            </motion.div>

            {/* Contact Support */}
            <motion.div 
              whileHover={{ y: -5 }}
              className="bg-white rounded-[3rem] p-10 shadow-xl border border-gray-100 relative overflow-hidden group"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-full -mr-16 -mt-16 opacity-50 group-hover:scale-110 transition-transform"></div>
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-blue-200">
                  <ChatBubbleLeftRightIcon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-black text-gray-900 mb-4 tracking-tight">Need Assistance?</h3>
                <p className="text-gray-500 font-medium mb-8 leading-relaxed">Our travel experts can help you process your booking manually or via alternative methods.</p>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center w-full px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-700 text-white font-black rounded-2xl transition-all shadow-xl shadow-blue-100 active:scale-95"
                >
                  <ChatBubbleLeftRightIcon className="w-5 h-5 mr-2" />
                  TALK TO EXPERT
                </Link>
              </div>
            </motion.div>
          </div>

          {/* Alternative Payment Methods */}
          <div className="bg-white rounded-[3rem] shadow-xl p-12 mb-12 border border-gray-100 relative overflow-hidden">
            <h3 className="text-2xl font-black text-gray-900 mb-10 text-center relative tracking-tight uppercase">Alternative Ways to Pay</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
              <div className="text-center group">
                <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-gray-100 group-hover:bg-orange-50 transition-colors">
                  <CreditCardIcon className="w-8 h-8 text-gray-400 group-hover:text-orange-500 transition-colors" />
                </div>
                <h4 className="font-black text-gray-900 mb-1 text-sm uppercase">Different Card</h4>
                <p className="text-[11px] text-gray-400 font-bold uppercase tracking-tighter">Instant Processing</p>
              </div>
              
              <div className="text-center group">
                <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-gray-100 group-hover:bg-orange-50 transition-colors">
                  <span className="text-3xl grayscale group-hover:grayscale-0 transition-all">🏦</span>
                </div>
                <h4 className="font-black text-gray-900 mb-1 text-sm uppercase">Bank Transfer</h4>
                <p className="text-[11px] text-gray-400 font-bold uppercase tracking-tighter">Direct & Secure</p>
              </div>
              
              <div className="text-center group">
                <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-gray-100 group-hover:bg-orange-50 transition-colors">
                  <PhoneIcon className="w-8 h-8 text-gray-400 group-hover:text-orange-500 transition-colors" />
                </div>
                <h4 className="font-black text-gray-900 mb-1 text-sm uppercase">Pay by Phone</h4>
                <p className="text-[11px] text-gray-400 font-bold uppercase tracking-tighter">Guided Support</p>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-gradient-to-br from-orange-500 to-yellow-500 rounded-[3rem] p-12 mb-12 text-white shadow-2xl shadow-orange-200 relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-white/20 via-transparent to-transparent"></div>
            <h3 className="text-2xl font-black mb-8 text-center flex items-center justify-center gap-3 relative tracking-tight">
              <PhoneIcon className="w-8 h-8" />
              Direct Support Hotline
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 relative">
              <a href="tel:+610212345678" className="bg-white/10 backdrop-blur-md rounded-3xl p-6 border border-white/20 hover:bg-white/20 transition-all group text-center">
                <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <PhoneIcon className="w-6 h-6 text-orange-600" />
                </div>
                <span className="block font-black text-2xl tracking-tight mb-1">+61 (02) 1234 5678</span>
                <span className="text-xs font-bold uppercase opacity-80">Mon-Fri 9AM-6PM AEST</span>
              </a>
              
              <a href="mailto:payments@baligetaway.com" className="bg-white/10 backdrop-blur-md rounded-3xl p-6 border border-white/20 hover:bg-white/20 transition-all group text-center">
                <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <EnvelopeIcon className="w-6 h-6 text-orange-600" />
                </div>
                <span className="block font-black text-xl tracking-tight mb-1 truncate">payments@baligetaway.com</span>
                <span className="text-xs font-bold uppercase opacity-80">Priority response within 2h</span>
              </a>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center mb-16">
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                to="/packages"
                className="inline-flex items-center justify-center px-10 py-5 bg-white text-gray-900 font-black rounded-[2rem] hover:bg-gray-50 transition-all shadow-xl border border-gray-100"
              >
                <ArrowRightIcon className="mr-3 w-6 h-6 text-gray-400" />
                EXPLORE PACKAGES
              </Link>
            </motion.div>
            
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                to="/"
                className="inline-flex items-center justify-center px-10 py-5 bg-gradient-to-r from-gray-800 to-gray-900 text-white font-black rounded-[2rem] transition-all shadow-2xl shadow-gray-200"
              >
                <HomeIcon className="mr-3 w-6 h-6" />
                BACK TO HOME
              </Link>
            </motion.div>
          </div>

          {/* Reassurance Note */}
          <div className="text-center pt-8 border-t border-gray-200/60">
            <div className="inline-flex items-center gap-2 text-gray-400 text-xs font-bold uppercase tracking-widest bg-gray-50 px-6 py-2 rounded-full border border-gray-100 shadow-sm">
              <ShieldCheckIcon className="w-4 h-4 text-green-500" />
              Bank-Level Encryption Secure
            </div>
            <p className="mt-6 text-sm text-gray-500 font-medium max-w-lg mx-auto leading-relaxed">
              No payment information was stored. We use industry-standard SSL encryption to ensure your data remains private and secure.
            </p>
          </div>
        </motion.div>
      </div>
    </>
  );
};

export default PaymentFailurePage;
